/**
 * <EarningsSurprise> — 어제 실적 발표 결과 (Beat / Miss)
 * earnings-calendar 가 "예정"이라면 surprise 는 "결과".
 * 데이터: GET /api/ticker/market/earnings-surprise → { items: [...] }
 */
"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import type { WidgetMeta, WidgetProps } from "../types";
import { koreanCompanyName } from "@/lib/korean-company-name";

interface SurpriseItem {
  symbol: string;
  name?: string | null;
  reportDate: string;
  estimate: number | null;
  actual: number | null;
  surprisePercent: number | null;
  changePercent: number | null;
}

const REFRESH_MS = 30 * 60 * 1000;

function fmtPct(v: number | null) {
  if (v == null || !Number.isFinite(v)) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${v.toFixed(1)}%`;
}
function fmtNum(v: number | null) {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(2);
}

export function EarningsSurprise(_props: WidgetProps) {
  const [items, setItems] = useState<SurpriseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        const res = await fetch("/api/ticker/market/earnings-surprise", { credentials: "include" });
        if (!res.ok) {
          if (mountedRef.current) { setError("로드 실패"); setLoading(false); }
          return;
        }
        const d = await res.json();
        if (mountedRef.current) {
          setItems(Array.isArray(d.items) ? d.items : []);
          setError(null);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) { setError("네트워크 오류"); setLoading(false); }
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { mountedRef.current = false; clearInterval(t); };
  }, []);

  return (
    <div className="dw-body dw-es">
      <div className="dw-head"><span className="dw-title">실적 발표 결과</span></div>
      {error ? (
        <div className="dw-es-error">{error}</div>
      ) : loading && items.length === 0 ? (
        <div className="dw-es-empty">로딩 중…</div>
      ) : items.length === 0 ? (
        <div className="dw-es-empty">
          <div style={{ fontWeight: 600, marginBottom: 4 }}>준비 중</div>
          <div style={{ fontSize: 10, color: "var(--bs-gray-500)" }}>
            데이터 source 작업 중<br />
            (Yahoo earnings · Alpha Vantage)
          </div>
        </div>
      ) : (
        <div className="dw-es-list">
          {items.slice(0, 8).map((it) => {
            const sp = it.surprisePercent;
            const beat = sp != null && sp > 0;
            const miss = sp != null && sp < 0;
            return (
              <Link
                key={`${it.symbol}-${it.reportDate}`}
                href={`/analyze/${encodeURIComponent(it.symbol)}`}
                className="dw-es-row"
                title={`${it.symbol} · ${koreanCompanyName(it.symbol, it.name)}`}
              >
                <span className="dw-es-name-block">
                  <span className="dw-es-sym">{koreanCompanyName(it.symbol, it.name)}</span>
                  <span className="dw-es-ticker">{it.symbol}</span>
                </span>
                <span className={`dw-es-tag ${beat ? "up" : miss ? "down" : ""}`}>
                  {beat ? "BEAT" : miss ? "MISS" : "—"}
                </span>
                <span className="dw-es-vals">
                  EPS {fmtNum(it.actual)} <span className="dw-es-est">vs {fmtNum(it.estimate)}</span>
                </span>
                <span className={`dw-es-pct ${beat ? "up" : miss ? "down" : ""}`}>{fmtPct(sp)}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const earningsSurpriseMeta: WidgetMeta = {
  type: "earnings-surprise",
  name: "실적 발표 결과",
  description: "어제 발표한 EPS Beat / Miss · earnings-calendar 짝꿍",
  icon: "💰",
  category: "discovery",
  defaultSize: { w: 6, h: 5 },
  availableSizes: [
    { w: 4, h: 5 },
    { w: 6, h: 5 },
    { w: 6, h: 7 },
  ],
  minW: 3,
  maxW: 12,
  minH: 4,
  maxH: 8,
  component: EarningsSurprise,
};
